// Data layer for the Boomin' redesign screens.
//
// Wraps the legacy /api/public/* endpoints (TopHandler, HubsLatLngHandler,
// HubHandler, CouponHandler) plus a handful of authenticated /api/users/*
// endpoints used by the wallet/active screens. Keeps all redesign API access
// in one place so the screens can stay focused on rendering.

import { http } from "../../http";
import {
  API_BASE_URL,
  GOOGLE_GEOCODE_API_KEY,
  LOCALSTORAGE_TOKEN_USER,
} from "../../Constants";
import { Coupon, Hub, HubsResponse } from "../../Types/App";
import { PictName } from "./shared";

// ── Shared types ──────────────────────────────────────────────────────────

export type Position = { lat: number; lng: number };

export type TopResponse = {
  hubs: Hub[];
  coupons: Coupon[];
  lat: number;
  lng: number;
};

export type HubsByLatLngResponse = {
  hubs: Hub[];
};

export type HubDetailResponse = {
  hub: Hub;
  coupons: Coupon[];
};

export type CouponDetailResponse = {
  hub: Hub;
  coupon: Coupon;
  available: boolean;
};

// /api/users/coupons/favorites/list payload (see models.CouponFavoriteData)
export type FavoriteItem = {
  id: number;
  image: string;
  title: string;
  description: string;
  created: string;
  hub_image: string;
  hub_name: string;
};
export type FavoritesResponse = { coupons: FavoriteItem[] };

// /api/users/coupons/used/list payload (see models.CouponUsed). Used list has
// no coupon id — we only show it as history, no detail link.
export type UsedItem = {
  image: string;
  title: string;
  description: string;
  user_id: number;
  profile_image: string;
  from_qr_code: boolean;
  created: string;
};
export type UsedListResponse = { result: UsedItem[] };

// /api/users/profile payload (see ProfileHandler). Powers the /me screen.
export type UserProfile = {
  user_name: string;
  profile_image: string;
  state: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  birth_date: string;
  gender: string;
  city: string;
  address_line_1: string;
  address_line_2: string;
  postal_code: string;
};
export type ProfileResponse = { favorite_count: number; user: UserProfile };

// ── Auth helper ───────────────────────────────────────────────────────────

export const isUserLoggedIn = (): boolean => {
  try {
    return !!localStorage.getItem(LOCALSTORAGE_TOKEN_USER);
  } catch {
    return false;
  }
};

// ── Public endpoints ──────────────────────────────────────────────────────

export const fetchTop = async (): Promise<TopResponse> => {
  const res = await http<TopResponse>(`${API_BASE_URL}/public/top`, "GET");
  if (!res.parsedBody) throw new Error("response error");
  return res.parsedBody;
};

export const fetchHubsByLatLng = async (
  lat: number,
  lng: number,
  radius: number
): Promise<Hub[]> => {
  const res = await http<HubsResponse>(`${API_BASE_URL}/public/hubs`, "POST", {
    lat,
    lng,
    radius,
  });
  return res.parsedBody?.hubs ?? [];
};

export const fetchHub = async (id: string | number): Promise<HubDetailResponse> => {
  const res = await http<HubDetailResponse>(
    `${API_BASE_URL}/public/hubs/${id}`,
    "GET"
  );
  if (!res.parsedBody) throw new Error("response error");
  return res.parsedBody;
};

export const fetchCoupon = async (
  id: string | number
): Promise<CouponDetailResponse> => {
  const res = await http<CouponDetailResponse>(
    `${API_BASE_URL}/public/users/coupons/${id}`,
    "GET"
  );
  if (!res.parsedBody) throw new Error("response error");
  return res.parsedBody;
};

// ── Authenticated endpoints (graceful no-op when logged out) ──────────────

export const fetchFavorites = async (): Promise<FavoriteItem[]> => {
  if (!isUserLoggedIn()) return [];
  const res = await http<FavoritesResponse>(
    `${API_BASE_URL}/users/coupons/favorites/list`,
    "GET"
  );
  return res.parsedBody?.coupons ?? [];
};

export const fetchUsedList = async (): Promise<UsedItem[]> => {
  if (!isUserLoggedIn()) return [];
  const res = await http<UsedListResponse>(
    `${API_BASE_URL}/users/coupons/used/list`,
    "GET"
  );
  return res.parsedBody?.result ?? [];
};

export const fetchProfile = async (): Promise<ProfileResponse> => {
  const res = await http<ProfileResponse>(
    `${API_BASE_URL}/users/profile`,
    "GET"
  );
  if (!res.parsedBody) throw new Error("response error");
  return res.parsedBody;
};

// Fields the /users/profile/update endpoint accepts. Note the server ignores
// `email` on update (the legacy form never sent it), so we treat it as
// read-only in the editor and omit it here.
export type ProfileUpdate = {
  user_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  birth_date: string;
  gender: string;
  state: string;
  city: string;
  address_line_1: string;
  address_line_2: string;
  postal_code: string;
  profile_image: string;
};

export const updateProfile = async (data: ProfileUpdate): Promise<void> => {
  if (!isUserLoggedIn()) throw new Error("login required");
  // Normalise the date to the RFC3339 shape the API expects.
  const birth_date =
    data.birth_date && data.birth_date.indexOf("Z") === -1
      ? `${data.birth_date}T00:00:00Z`
      : data.birth_date;
  await http(`${API_BASE_URL}/users/profile/update`, "POST", {
    ...data,
    birth_date,
  });
};

// Clear the user session and return to the landing page.
export const logoutUser = (): void => {
  try {
    localStorage.removeItem(LOCALSTORAGE_TOKEN_USER);
  } catch {
    /* ignore */
  }
  window.location.href = "/";
};

// Mark a coupon as used. Named `consumeCoupon` (not `useCoupon`) because the
// `use*` prefix triggers react-hooks/rules-of-hooks lint false-positives.
export const consumeCoupon = async (id: string | number): Promise<void> => {
  if (!isUserLoggedIn()) throw new Error("login required");
  await http(`${API_BASE_URL}/users/coupons/${id}/use`, "POST", {});
};

// Add/remove a coupon to the logged-in user's favorites. The server binds a
// numeric `coupon_id`, so coerce the route param to a number before sending.
export const addFavorite = async (id: string | number): Promise<void> => {
  if (!isUserLoggedIn()) throw new Error("login required");
  await http(`${API_BASE_URL}/users/coupons/favorites/add`, "POST", {
    coupon_id: Number(id),
  });
};

export const removeFavorite = async (id: string | number): Promise<void> => {
  if (!isUserLoggedIn()) throw new Error("login required");
  await http(`${API_BASE_URL}/users/coupons/favorites/remove`, "POST", {
    coupon_id: Number(id),
  });
};

// Whether the given coupon is already in the user's favorites. The favorites
// list keys on the coupon id (see models.CouponFavoriteList), so we match on it.
export const isCouponFavorited = async (
  id: string | number
): Promise<boolean> => {
  if (!isUserLoggedIn()) return false;
  const list = await fetchFavorites();
  return list.some((f) => f.id === Number(id));
};

// ── Geolocation + reverse-geocoding ───────────────────────────────────────

export const getCurrentPosition = (): Promise<Position> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation unsupported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => reject(e),
      { maximumAge: 60_000, timeout: 8_000, enableHighAccuracy: false }
    );
  });

// Forward-geocode an area name (e.g. "千駄ヶ谷", "渋谷区") to a lat/lng.
// We bias toward Tokyo via a `components` filter so generic place names resolve
// to the expected Tokyo neighbourhood. Returns null on failure; callers should
// fall back to a hardcoded preset or the user's current location.
export const forwardGeocode = async (
  area: string
): Promise<Position | null> => {
  if (!GOOGLE_GEOCODE_API_KEY || !area) return null;
  try {
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?address=${encodeURIComponent(area)}` +
      `&components=country:JP|administrative_area:Tokyo` +
      `&language=ja&key=${GOOGLE_GEOCODE_API_KEY}`;
    const r = await fetch(url);
    const body = await r.json();
    const loc = body?.results?.[0]?.geometry?.location;
    if (loc && typeof loc.lat === "number" && typeof loc.lng === "number") {
      return { lat: loc.lat, lng: loc.lng };
    }
    return null;
  } catch {
    return null;
  }
};

// Hardcoded coordinates for areas surfaced in the UI (footer links, area
// picker). Lets us render /area/:name without depending on the geocoding API
// being available, and gives the area picker an instant response.
export const PRESET_AREAS: { name: string; lat: number; lng: number }[] = [
  { name: "渋谷区", lat: 35.6580, lng: 139.7016 },
  { name: "新宿区", lat: 35.6896, lng: 139.7006 },
  { name: "千駄ヶ谷", lat: 35.6814, lng: 139.7115 },
  { name: "原宿", lat: 35.6702, lng: 139.7026 },
  { name: "恵比寿", lat: 35.6464, lng: 139.7100 },
  { name: "代々木", lat: 35.6837, lng: 139.7022 },
];

export const lookupPresetArea = (name: string): Position | null => {
  const m = PRESET_AREAS.find((a) => a.name === name);
  return m ? { lat: m.lat, lng: m.lng } : null;
};

// Forward-lookup a Japanese postal code → address parts, so the profile
// editor can auto-fill 都道府県 / 市区町村 / 番地. Mirrors the legacy
// utils.autoCompleteFields parsing. Returns null on failure.
export type PostalAddress = { state: string; city: string; addressLine1: string };

export const lookupPostalCode = async (
  postalCode: string
): Promise<PostalAddress | null> => {
  if (!GOOGLE_GEOCODE_API_KEY || !postalCode) return null;
  try {
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?address=${encodeURIComponent(postalCode)}` +
      `&language=ja&components=country:JP&key=${GOOGLE_GEOCODE_API_KEY}`;
    const r = await fetch(url);
    const body = await r.json();
    const components: Array<{ long_name: string; types: string[] }> =
      body?.results?.[0]?.address_components ?? [];
    if (!components.length) return null;
    let addressLine1 = "";
    let state = "";
    let city = "";
    components.forEach((e) => {
      if (
        e.types.includes("sublocality_level_4") ||
        e.types.includes("sublocality_level_3") ||
        e.types.includes("sublocality_level_2") ||
        e.types.includes("sublocality_level_1")
      ) {
        addressLine1 = e.long_name + addressLine1;
      } else if (
        e.types.includes("locality") ||
        e.types.includes("administrative_area_level_2")
      ) {
        city = e.long_name + city;
      } else if (e.types.includes("administrative_area_level_1")) {
        state = e.long_name + state;
      }
    });
    return { state, city, addressLine1 };
  } catch {
    return null;
  }
};

// Best-effort reverse-geocode → "区・町名" string. Returns "" on failure so
// the UI can fall back to the hub address or a generic label.
export const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
  if (!GOOGLE_GEOCODE_API_KEY) return "";
  try {
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?latlng=${lat},${lng}&language=ja&key=${GOOGLE_GEOCODE_API_KEY}`;
    const r = await fetch(url);
    const body = await r.json();
    const components: Array<{ long_name: string; types: string[] }> =
      body?.results?.[0]?.address_components ?? [];
    let ward = "";
    let town = "";
    components.forEach((c) => {
      if (c.types.includes("locality")) ward = c.long_name;
      else if (c.types.includes("sublocality_level_1") && !ward) ward = c.long_name;
      else if (c.types.includes("sublocality_level_2") && !town) town = c.long_name;
    });
    if (ward && town) return `${ward}・${town}`;
    return ward || town || "";
  } catch {
    return "";
  }
};

// ── Google Maps script loader options ─────────────────────────────────────
// Shared so the JS API loads once per page-load even though several redesign
// screens render their own GoogleMap.

export const GOOGLE_MAPS_LOADER_ID = "boomin-google-maps";
export const GOOGLE_MAPS_LIBRARIES: ("places")[] = [];

// ── Distance + display helpers ────────────────────────────────────────────

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;

// Great-circle distance between two coordinates in kilometres.
export const haversineKm = (a: Position, b: Position): number => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

// Roughly 80m/min for "casual walk". Round up so "0.1km away" still shows "徒歩2分".
export const walkMinutes = (km: number): number =>
  Math.max(1, Math.ceil((km * 1000) / 80));

export const formatWalk = (km: number): string => `徒歩${walkMinutes(km)}分`;
export const formatDistance = (km: number): string =>
  km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`;

// ── Coupon → display helpers ──────────────────────────────────────────────

export const couponHubName = (c: Coupon, hub?: Hub | null): string =>
  c.hub_name || hub?.name || "";

export const couponHubArea = (c: Coupon, hub?: Hub | null): string => {
  const state = c.hub_state || hub?.state || "";
  const city = c.hub_city || hub?.city || "";
  if (state && city) return `${state}・${city}`;
  return state || city || "";
};

export const couponHubLat = (c: Coupon, hub?: Hub | null): number =>
  c.hub_latitude || hub?.latitude || 0;

export const couponHubLng = (c: Coupon, hub?: Hub | null): number =>
  c.hub_longitude || hub?.longitude || 0;

// Parse the API's ISO-ish period_end into a friendly Japanese expiry label,
// preferring "今日 HH:mm まで" when the end is today.
export const formatExpiry = (periodEnd: string | undefined): string => {
  if (!periodEnd) return "";
  const t = new Date(periodEnd);
  if (Number.isNaN(t.getTime())) return "";
  const now = new Date();
  const sameDay =
    t.getFullYear() === now.getFullYear() &&
    t.getMonth() === now.getMonth() &&
    t.getDate() === now.getDate();
  if (sameDay) {
    const hh = String(t.getHours()).padStart(2, "0");
    const mm = String(t.getMinutes()).padStart(2, "0");
    return `本日 ${hh}:${mm}まで`;
  }
  const days = ["日", "月", "火", "水", "木", "金", "土"];
  return `${t.getFullYear()}/${t.getMonth() + 1}/${t.getDate()} (${days[t.getDay()]}) まで`;
};

export const isExpiringToday = (periodEnd: string | undefined): boolean => {
  if (!periodEnd) return false;
  const t = new Date(periodEnd);
  if (Number.isNaN(t.getTime())) return false;
  const now = new Date();
  return (
    t.getFullYear() === now.getFullYear() &&
    t.getMonth() === now.getMonth() &&
    t.getDate() === now.getDate()
  );
};

// Pick a sensible "tag" pill label for a coupon based on its remaining time.
export const couponTag = (periodEnd: string | undefined): string => {
  if (!periodEnd) return "クーポン";
  const t = new Date(periodEnd);
  if (Number.isNaN(t.getTime())) return "クーポン";
  const ms = t.getTime() - Date.now();
  const days = ms / (1000 * 60 * 60 * 24);
  if (days < 0) return "終了";
  if (days < 1) return "本日まで";
  if (days < 4) return `残り${Math.ceil(days)}日`;
  if (days < 14) return "今だけ";
  return "人気";
};

// ── Category heuristic ────────────────────────────────────────────────────
// The DB has no category field, so we infer one from the coupon/hub name.
// Worst case we fall back to a generic "shop" pictogram.

export type RedesignCategory =
  | "cafe"
  | "meal"
  | "sweet"
  | "bar"
  | "shop"
  | "salon"
  | "activity";

export const categoryLabel = (cat: RedesignCategory): string =>
  ({
    cafe: "カフェ",
    meal: "ごはん",
    sweet: "スイーツ",
    bar: "バー",
    shop: "ショップ",
    salon: "サロン",
    activity: "体験",
  }[cat]);

// The "meal" category reuses the ramen-bowl pictogram; every other category
// shares its name with its pictogram.
export const categoryPict = (cat: RedesignCategory): PictName =>
  cat === "meal" ? "ramen" : cat;

const CATEGORY_VALUES: Record<string, RedesignCategory> = {
  cafe: "cafe",
  meal: "meal",
  sweet: "sweet",
  bar: "bar",
  shop: "shop",
  salon: "salon",
  activity: "activity",
};

// Prefer the explicit `coupons.category` column populated by the admin tool;
// fall back to a regex on title/description so screens still light up correctly
// for any coupon that slipped through the admin backfill.
export const inferCategory = (c: Coupon, hub?: Hub | null): RedesignCategory => {
  if (c.category && CATEGORY_VALUES[c.category]) return CATEGORY_VALUES[c.category];
  const text = `${c.title || ""} ${c.description || ""} ${
    c.hub_name || hub?.name || ""
  }`.toLowerCase();
  if (/(cafe|カフェ|coffee|コーヒー|珈琲)/.test(text)) return "cafe";
  if (/(ラーメン|麺|うどん|そば|定食|食堂|ごはん|ランチ|丼)/.test(text)) return "meal";
  if (/(スイーツ|ケーキ|パン|プリン|アイス|ベーカリー|和菓子|洋菓子)/.test(text)) return "sweet";
  if (/(bar|バー|居酒屋|酒|ワイン|ビール|cocktail|カクテル)/.test(text)) return "bar";
  if (/(サロン|美容|ヘア|ネイル|エステ|マッサージ)/.test(text)) return "salon";
  if (/(体験|アクティビティ|ヨガ|スタジオ)/.test(text)) return "activity";
  return "shop";
};
